
import React, { Component, ErrorInfo, ReactNode } from 'react';
import { AlertTriangle, RefreshCcw } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return { hasError: true, error, errorInfo: null };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({
      error,
      errorInfo,
    });
    
    // Log error to console (in production, you might want to log to a service)
    console.error('Error caught by ErrorBoundary:', error, errorInfo);
  }
  
  handleRetry = (): void => {
    window.location.reload();
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }
      
      return (
        <div className="min-h-screen flex items-center justify-center bg-gray-50">
          <div className="bg-white p-8 rounded-lg shadow-md max-w-md w-full text-center">
            <div className="mb-6 flex justify-center">
              <div className="p-3 bg-red-100 rounded-full">
                <AlertTriangle size={40} className="text-red-600" />
              </div>
            </div>
            <h2 className="text-2xl font-bold text-gray-800 mb-4">Something went wrong</h2>
            <p className="text-gray-600 mb-6">
              We're sorry, but there was an error loading this page. Our team has been notified of the issue.
            </p>
            <div className="flex flex-col gap-3">
              <button 
                onClick={this.handleRetry}
                className="bg-primary text-white px-6 py-3 rounded-md font-medium flex items-center justify-center gap-2 hover:bg-primary/90 transition-colors"
              >
                <RefreshCcw size={18} />
                Try Again
              </button>
              <a 
                href="/"
                className="text-primary hover:text-primary/80 transition-colors"
              >
                Return to Home Page
              </a>
            </div>
            
            {process.env.NODE_ENV !== 'production' && this.state.error && (
              <div className="mt-6 p-4 bg-gray-100 rounded-md text-left overflow-auto max-h-40">
                <p className="text-red-600 font-mono text-sm whitespace-pre-wrap">
                  {this.state.error.toString()}
                </p>
                {this.state.errorInfo && (
                  <details className="mt-2">
                    <summary className="text-sm cursor-pointer text-gray-700">Stack trace</summary>
                    <p className="text-xs font-mono whitespace-pre-wrap text-gray-600 mt-2">
                      {this.state.errorInfo.componentStack}
                    </p>
                  </details>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
